import { paths } from '@/generated/supplier-experience-api';
import { downloadCsvFromPost } from '@/utils/downloadCsvFromPost';
import { LockedInvoiceFetchOptions, LockedInvoiceRequestBody, LockedInvoiceSearchFilters } from './useLockedInvoices';
import { TakerMarket } from './useTakerMarkets';

export type LockedInvoicesCsvQuery = NonNullable<paths['/locked-invoices/csv']['post']['parameters']['query']>;

export interface DownloadLockedInvoicesCsvArgs {
  makerOrganizationUuid: string;
  takerMarkets: TakerMarket[];
  search?: LockedInvoiceSearchFilters;
  /** Sort only — CSV returns the full filtered set (no cursor/limit). */
  options?: Pick<NonNullable<LockedInvoiceFetchOptions>, 'sortBy' | 'sortDirection'>;
  filename: string;
}

/**
 * Download locked (ineligible) invoices CSV via SEA.
 * Body/query match the locked list API (same shape as `useLockedInvoices`).
 */
export const downloadLockedInvoicesCsv = async ({
  makerOrganizationUuid,
  takerMarkets,
  search,
  options,
  filename,
}: DownloadLockedInvoicesCsvArgs): Promise<void> => {
  const body = {
    makerOrganizationUuid,
    supplierMarkets: takerMarkets.map((takerMarket) => ({
      marketUuid: takerMarket.marketUuid,
      supplierDivisionUuid: takerMarket.takerDivisionUuid,
    })),
    ...(search ? { search } : {}),
  } satisfies LockedInvoiceRequestBody;

  const query: LockedInvoicesCsvQuery = {
    ...(options?.sortBy && { sortBy: options.sortBy }),
    ...(options?.sortDirection && { sortDirection: options.sortDirection }),
  };

  await downloadCsvFromPost({
    path: 'api/supplier-experience/locked-invoices/csv',
    body,
    query: Object.keys(query).length > 0 ? query : undefined,
    filename,
  });
};
